import { useState, useEffect, useRef } from "react";

const POLKADOT_PINK = "#E6007A";
const STASH = "1jt73DNw1xXpz38GhDGFun3eX585wtzFkmkVLs8TKCA7bJG";

// --- Animated counter ---
function useCounter(target, duration = 2000, start = false) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      setValue(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return value;
}

// --- Intersection observer hook ---
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

// --- Section wrapper with fade-in ---
function Section({ children, className = "", id }) {
  const [ref, inView] = useInView(0.08);
  return (
    <section
      ref={ref}
      id={id}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(32px)",
        transition:
          "opacity 0.7s cubic-bezier(.4,0,.2,1), transform 0.7s cubic-bezier(.4,0,.2,1)",
      }}
    >
      {children}
    </section>
  );
}

// --- Spec card ---
function SpecCard({ icon, label, value, detail, delay = 0, inView }) {
  return (
    <div
      style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.07)",
        borderRadius: "12px",
        padding: "28px 24px",
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(24px)",
        transition: `all 0.6s cubic-bezier(.4,0,.2,1) ${delay}ms`,
      }}
    >
      <div style={{ fontSize: "28px", marginBottom: "12px" }}>{icon}</div>
      <div
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: "11px",
          letterSpacing: "2px",
          color: "rgba(255,255,255,0.4)",
          textTransform: "uppercase",
          marginBottom: "6px",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontSize: "20px",
          fontWeight: 700,
          color: "#fff",
          marginBottom: "4px",
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: "12px",
          color: "rgba(255,255,255,0.35)",
          lineHeight: 1.5,
        }}
      >
        {detail}
      </div>
    </div>
  );
}

// --- Reliability feature ---
function ReliabilityFeature({ icon, title, description, delay = 0, inView }) {
  return (
    <div
      style={{
        display: "flex",
        gap: "20px",
        alignItems: "flex-start",
        opacity: inView ? 1 : 0,
        transform: inView ? "translateX(0)" : "translateX(-20px)",
        transition: `all 0.5s cubic-bezier(.4,0,.2,1) ${delay}ms`,
      }}
    >
      <div
        style={{
          width: "48px",
          height: "48px",
          borderRadius: "10px",
          background: `linear-gradient(135deg, ${POLKADOT_PINK}22, ${POLKADOT_PINK}08)`,
          border: `1px solid ${POLKADOT_PINK}33`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "22px",
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div>
        <div
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 600,
            fontSize: "16px",
            color: "#fff",
            marginBottom: "4px",
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "14px",
            color: "rgba(255,255,255,0.5)",
            lineHeight: 1.6,
          }}
        >
          {description}
        </div>
      </div>
    </div>
  );
}

// --- Track record card ---
function TrackCard({
  network,
  identity,
  address,
  role,
  period,
  subscanBase,
  delay = 0,
  inView,
}) {
  return (
    <div
      style={{
        background: "rgba(255,255,255,0.025)",
        border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: "12px",
        padding: "24px",
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(20px)",
        transition: `all 0.5s cubic-bezier(.4,0,.2,1) ${delay}ms`,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          marginBottom: "14px",
        }}
      >
        <div
          style={{
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            background: network === "Polkadot" ? POLKADOT_PINK : "#000",
            border: network === "Kusama" ? "2px solid #fff" : "none",
          }}
        />
        <span
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 600,
            fontSize: "14px",
            color: "#fff",
          }}
        >
          {network}
        </span>
      </div>
      <div
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: "13px",
          color: POLKADOT_PINK,
          marginBottom: "6px",
        }}
      >
        {identity}
      </div>
      <div
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: "11px",
          color: "rgba(255,255,255,0.3)",
          wordBreak: "break-all",
          marginBottom: "12px",
        }}
      >
        {address}
      </div>
      <div
        style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "13px",
          color: "rgba(255,255,255,0.5)",
          marginBottom: "4px",
        }}
      >
        {role}
      </div>
      <div
        style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "13px",
          color: "rgba(255,255,255,0.4)",
        }}
      >
        {period}
      </div>
      {subscanBase && (
        <a
          href={`https://${subscanBase}.subscan.io/validator/${address}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "inline-block",
            marginTop: "12px",
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: "11px",
            color: POLKADOT_PINK,
            textDecoration: "none",
            borderBottom: `1px solid ${POLKADOT_PINK}44`,
          }}
        >
          View on Subscan →
        </a>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN APP
// ─────────────────────────────────────────────
export default function SafeStakeValidator() {
  const [heroVisible, setHeroVisible] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [specRef, specInView] = useInView(0.1);
  const [relRef, relInView] = useInView(0.1);
  const [trackRef, trackInView] = useInView(0.1);
  const [nomRef, nomInView] = useInView(0.1);

  const cores = useCounter(20, 1200, heroVisible);
  const ram = useCounter(64, 1200, heroVisible);
  const storage = useCounter(8, 1000, heroVisible);
  const selfStake = useCounter(11850, 1800, heroVisible);

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 200);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      style={{
        background: "#0a0a0c",
        color: "#fff",
        minHeight: "100vh",
        fontFamily: "'DM Sans', sans-serif",
        overflowX: "hidden",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600;700&family=Space+Grotesk:wght@400;500;600;700&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { background: #0a0a0c; }
        a { color: inherit; }
        ::selection { background: ${POLKADOT_PINK}44; color: #fff; }
        @keyframes pulse-dot { 0%, 100% { opacity: 1; box-shadow: 0 0 8px ${POLKADOT_PINK}88; } 50% { opacity: 0.6; box-shadow: 0 0 16px ${POLKADOT_PINK}44; } }
        @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        @keyframes scan-line { 0% { top: -2px; } 100% { top: 100%; } }
        @media (max-width: 768px) {
          .nav-links { display: none !important; }
          .nav-links.open { display: flex !important; flex-direction: column; position: fixed; top: 64px; left: 0; right: 0; background: rgba(10,10,12,0.95); backdrop-filter: blur(20px); padding: 24px 40px; gap: 24px !important; border-bottom: 1px solid rgba(255,255,255,0.05); }
          .hamburger { display: flex !important; }
        }
        @media (min-width: 769px) {
          .hamburger { display: none !important; }
        }
      `}</style>

      {/* ── NAV ── */}
      <nav
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          background: "rgba(10,10,12,0.85)",
          backdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(255,255,255,0.05)",
          padding: "0 40px",
          height: "64px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            <img
              src="/logo.png"
              alt="SafeStake"
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </div>
          <span
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 600,
              fontSize: "24px",
              letterSpacing: "-0.3px",
            }}
          >
            SafeStake
          </span>
        </div>
        <button
          className="hamburger"
          onClick={() => setMenuOpen(!menuOpen)}
          style={{
            display: "none",
            background: "none",
            border: "none",
            cursor: "pointer",
            flexDirection: "column",
            gap: "5px",
            padding: "8px",
          }}
        >
          <span
            style={{
              display: "block",
              width: "24px",
              height: "2px",
              background: "#fff",
              transition: "all 0.3s",
              transform: menuOpen ? "rotate(45deg) translateY(7px)" : "none",
            }}
          />
          <span
            style={{
              display: "block",
              width: "24px",
              height: "2px",
              background: "#fff",
              transition: "all 0.3s",
              opacity: menuOpen ? 0 : 1,
            }}
          />
          <span
            style={{
              display: "block",
              width: "24px",
              height: "2px",
              background: "#fff",
              transition: "all 0.3s",
              transform: menuOpen ? "rotate(-45deg) translateY(-7px)" : "none",
            }}
          />
        </button>
        <div
          className={`nav-links ${menuOpen ? "open" : ""}`}
          style={{ display: "flex", gap: "32px", alignItems: "center" }}
        >
          {["Infrastructure", "Reliability", "Track Record", "Nominate"].map(
            (item) => (
              <a
                key={item}
                href={`#${item.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setMenuOpen(false)}
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "13px",
                  color: "rgba(255,255,255,0.5)",
                  textDecoration: "none",
                  transition: "color 0.2s",
                }}
                onMouseEnter={(e) => (e.target.style.color = "#fff")}
                onMouseLeave={(e) =>
                  (e.target.style.color = "rgba(255,255,255,0.5)")
                }
              >
                {item}
              </a>
            ),
          )}
        </div>
      </nav>

      {/* ── HERO ── */}
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "120px 0 80px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background grid */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            opacity: 0.03,
            backgroundImage: `linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)`,
            backgroundSize: "80px 80px",
          }}
        />
        {/* Gradient glow */}
        <div
          style={{
            position: "absolute",
            top: "-20%",
            right: "-10%",
            width: "600px",
            height: "600px",
            borderRadius: "50%",
            background: `radial-gradient(circle, ${POLKADOT_PINK}12 0%, transparent 70%)`,
            filter: "blur(80px)",
          }}
        />

        <div
          style={{
            position: "relative",
            maxWidth: "1000px",
            margin: "0 auto",
            width: "100%",
            padding: "0 40px",
          }}
        >
          {/* Status badge */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: "100px",
              padding: "8px 18px",
              marginBottom: "32px",
              opacity: heroVisible ? 1 : 0,
              transform: heroVisible ? "translateY(0)" : "translateY(16px)",
              transition: "all 0.6s cubic-bezier(.4,0,.2,1) 0ms",
            }}
          >
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: "#22c55e",
                animation: "pulse-dot 2s ease-in-out infinite",
              }}
            />
            <span
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "12px",
                color: "rgba(255,255,255,0.6)",
              }}
            >
              Live · Polkadot Mainnet
            </span>
          </div>

          {/* Title */}
          <h1
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 700,
              fontSize: "clamp(48px, 7vw, 84px)",
              lineHeight: 1.05,
              letterSpacing: "-2px",
              marginBottom: "24px",
              opacity: heroVisible ? 1 : 0,
              transform: heroVisible ? "translateY(0)" : "translateY(24px)",
              transition: "all 0.7s cubic-bezier(.4,0,.2,1) 100ms",
            }}
          >
            SafeStake
          </h1>

          {/* Subtitle */}
          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "20px",
              color: "rgba(255,255,255,0.45)",
              lineHeight: 1.7,
              maxWidth: "700px",
              marginBottom: "48px",
              opacity: heroVisible ? 1 : 0,
              transform: heroVisible ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.7s cubic-bezier(.4,0,.2,1) 200ms",
            }}
          >
            Bare-metal Polkadot validator. Owner-operated from Houston, Texas on
            private infrastructure. No VPS. No data center. Real hardware, real
            redundancy, real security, real decentralization.
          </p>

          {/* Key metrics */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
              gap: "1px",
              background: "rgba(255,255,255,0.06)",
              borderRadius: "12px",
              overflow: "hidden",
              maxWidth: "780px",
              opacity: heroVisible ? 1 : 0,
              transform: heroVisible ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.7s cubic-bezier(.4,0,.2,1) 350ms",
            }}
          >
            {[
              { label: "Commission", value: "10%", sub: "Competitive rate" },
              {
                label: "Self-Stake",
                value: `${selfStake.toLocaleString()}+`,
                sub: "DOT bonded",
              },
              { label: "Slashing", value: "Zero", sub: "Lifetime events" },
              { label: "Since", value: "2023", sub: "1KV programme" },
            ].map((m) => (
              <div
                key={m.label}
                style={{ background: "#0a0a0c", padding: "20px 24px" }}
              >
                <div
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: "10px",
                    letterSpacing: "2px",
                    color: "rgba(255,255,255,0.3)",
                    textTransform: "uppercase",
                    marginBottom: "6px",
                  }}
                >
                  {m.label}
                </div>
                <div
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: "24px",
                    fontWeight: 700,
                    color: "#fff",
                  }}
                >
                  {m.value}
                </div>
                <div
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: "11px",
                    color: "rgba(255,255,255,0.25)",
                    marginTop: "2px",
                  }}
                >
                  {m.sub}
                </div>
              </div>
            ))}
          </div>

          {/* Stash address */}
          <div
            style={{
              marginTop: "32px",
              opacity: heroVisible ? 1 : 0,
              transition: "opacity 0.7s cubic-bezier(.4,0,.2,1) 500ms",
            }}
          >
            <div
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "10px",
                letterSpacing: "2px",
                color: "rgba(255,255,255,0.25)",
                textTransform: "uppercase",
                marginBottom: "6px",
              }}
            >
              Stash Address
            </div>
            <a
              href={`https://polkadot.subscan.io/account/${STASH}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "12px",
                color: "rgba(255,255,255,0.4)",
                textDecoration: "none",
                wordBreak: "break-all",
                borderBottom: "1px solid rgba(255,255,255,0.1)",
                paddingBottom: "2px",
              }}
            >
              {STASH}
            </a>
          </div>
        </div>
      </div>

      {/* ── INFRASTRUCTURE ── */}
      <Section id="infrastructure">
        <div
          style={{ maxWidth: "1000px", margin: "0 auto", padding: "80px 40px" }}
        >
          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "11px",
              letterSpacing: "3px",
              color: POLKADOT_PINK,
              textTransform: "uppercase",
              marginBottom: "12px",
            }}
          >
            Infrastructure
          </div>
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 700,
              fontSize: "36px",
              letterSpacing: "-1px",
              marginBottom: "12px",
            }}
          >
            Purpose-Built Hardware
          </h2>
          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "16px",
              color: "rgba(255,255,255,0.4)",
              maxWidth: "560px",
              lineHeight: 1.7,
              marginBottom: "48px",
            }}
          >
            Every component selected for validator workloads. Server-grade
            motherboard with ECC memory, NVMe RAID1 redundancy, and enterprise
            remote management — in a silent, thermally optimized enclosure.
          </p>

          <div
            ref={specRef}
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: "16px",
            }}
          >
            <SpecCard
              inView={specInView}
              delay={0}
              icon="⚡"
              label="Processor"
              value="Core Ultra 7 265K"
              detail="8 P-cores (5.5 GHz) + 12 E-cores (4.6 GHz) · 20 cores total · No hyperthreading"
            />
            <SpecCard
              inView={specInView}
              delay={80}
              icon="🛡️"
              label="Memory"
              value="64 GB DDR5 ECC"
              detail="Error-correcting · DDR5-5600 · Prevents silent data corruption"
            />
            <SpecCard
              inView={specInView}
              delay={160}
              icon="💾"
              label="Storage"
              value="2× 4TB NVMe RAID1"
              detail="Samsung 990 PRO · Software mirror · Survives single drive failure"
            />
            <SpecCard
              inView={specInView}
              delay={240}
              icon="🖥️"
              label="Motherboard"
              value="ASRock Rack W880D4U"
              detail="Server-grade · IPMI 2.0 · Remote KVM · Hardware monitoring"
            />
            <SpecCard
              inView={specInView}
              delay={320}
              icon="🔇"
              label="Cooling"
              value="Noctua NH-D15"
              detail="Dual tower · Near-silent · 44°C idle verified · Acoustically optimized"
            />
            <SpecCard
              inView={specInView}
              delay={400}
              icon="🔌"
              label="Power"
              value="Seasonic GX-850"
              detail="80+ Gold · 10-year warranty · ATX 3.0 · Fully modular"
            />
          </div>
        </div>
      </Section>

      {/* ── RELIABILITY ── */}
      <Section id="reliability">
        <div
          style={{ maxWidth: "1000px", margin: "0 auto", padding: "80px 40px" }}
        >
          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "11px",
              letterSpacing: "3px",
              color: POLKADOT_PINK,
              textTransform: "uppercase",
              marginBottom: "12px",
            }}
          >
            Reliability
          </div>
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 700,
              fontSize: "36px",
              letterSpacing: "-1px",
              marginBottom: "12px",
            }}
          >
            No Single Point of Failure
          </h2>
          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "16px",
              color: "rgba(255,255,255,0.4)",
              maxWidth: "560px",
              lineHeight: 1.7,
              marginBottom: "48px",
            }}
          >
            Redundancy at every layer: power, storage, network, and remote
            access. Security-hardened from kernel to service layer. Designed for
            unattended 24/7 operation with automatic recovery from outages.
          </p>

          <div
            ref={relRef}
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
              gap: "28px",
            }}
          >
            <ReliabilityFeature
              inView={relInView}
              delay={0}
              icon="🔋"
              title="Multi-Tier Power Protection"
              description="UPS with automated graceful shutdown ensures the validator stops cleanly before power loss and auto-restarts on recovery. A secondary lithium battery pack extends runtime through prolonged outages — multiple tiers of protection before the node ever goes dark."
            />
            <ReliabilityFeature
              inView={relInView}
              delay={100}
              icon="💿"
              title="NVMe RAID1 Mirror"
              description="Two Samsung 990 PRO 4TB drives in software RAID1. If one drive fails, the validator keeps running on the survivor with zero downtime while you replace the failed drive."
            />
            <ReliabilityFeature
              inView={relInView}
              delay={200}
              icon="🌐"
              title="Dual-ISP Connectivity"
              description="Dual-ISP connectivity with automated health-check script that detects outages and switches the default route — no manual intervention required."
            />
            <ReliabilityFeature
              inView={relInView}
              delay={300}
              icon="📡"
              title="IPMI Remote Management"
              description="Out-of-band access via dedicated BMC port. Remote KVM console, power control, and hardware sensor monitoring — even when the OS is completely unresponsive."
            />
            <ReliabilityFeature
              inView={relInView}
              delay={400}
              icon="🛡️"
              title="ECC Memory Protection"
              description="64GB of error-correcting DDR5 detects and corrects single-bit memory errors in real-time. Prevents silent data corruption that commodity hardware misses entirely."
            />
            <ReliabilityFeature
              inView={relInView}
              delay={500}
              icon="⚙️"
              title="Hybrid CPU Architecture"
              description="20-core Intel processor with 8 high-performance P-cores (5.5 GHz) and 12 efficiency cores (4.6 GHz). Benchmark passed all reference hardware categories at 173–665% of minimum thresholds — massive headroom for sustained validation workloads."
            />
            <ReliabilityFeature
              inView={relInView}
              delay={600}
              icon="🔒"
              title="Defense-in-Depth Hardening"
              description="Validator service sandboxed at systemd security score 3.0 (from 9.2 baseline). SSH key-only, Fail2Ban, UFW firewall, monitoring behind WireGuard VPN with authenticated reverse proxy. No exposed attack surface — every layer locked down."
            />
          </div>
        </div>
      </Section>

      {/* ── WHY DECENTRALIZATION MATTERS ── */}
      <Section>
        <div
          style={{
            maxWidth: "1000px",
            margin: "0 auto",
            padding: "80px 40px",
            borderTop: "1px solid rgba(255,255,255,0.05)",
          }}
        >
          <div
            style={{
              background: `linear-gradient(135deg, ${POLKADOT_PINK}08, rgba(255,255,255,0.02))`,
              border: `1px solid ${POLKADOT_PINK}18`,
              borderRadius: "16px",
              padding: "48px 40px",
            }}
          >
            <h3
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 700,
                fontSize: "24px",
                letterSpacing: "-0.5px",
                marginBottom: "16px",
              }}
            >
              Why This Matters for Polkadot
            </h3>
            <p
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "15px",
                color: "rgba(255,255,255,0.5)",
                lineHeight: 1.8,
                maxWidth: "700px",
              }}
            >
              Most validators run on a handful of cloud providers — AWS,
              Hetzner, OVH. When one provider has an outage, dozens of
              validators go offline simultaneously. That's a centralization risk
              that undermines the entire point of a decentralized network.
            </p>
            <p
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "15px",
                color: "rgba(255,255,255,0.5)",
                lineHeight: 1.8,
                maxWidth: "700px",
                marginTop: "12px",
              }}
            >
              SafeStake runs on owner-operated bare metal in Houston, Texas —
              not in a data center, not on shared infrastructure, not subject to
              a cloud provider's terms of service or region-wide failures. The
              validator service is security-hardened from kernel to application
              layer, with monitoring encrypted end-to-end through a VPN tunnel.
              When you nominate this validator, you're actively strengthening
              Polkadot's geographic diversity, infrastructure independence, and
              operational security posture.
            </p>
          </div>
        </div>
      </Section>

      {/* ── TRACK RECORD ── */}
      <Section id="track-record">
        <div
          style={{ maxWidth: "1000px", margin: "0 auto", padding: "80px 40px" }}
        >
          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "11px",
              letterSpacing: "3px",
              color: POLKADOT_PINK,
              textTransform: "uppercase",
              marginBottom: "12px",
            }}
          >
            Track Record
          </div>
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 700,
              fontSize: "36px",
              letterSpacing: "-1px",
              marginBottom: "12px",
            }}
          >
            Proven Operator
          </h2>
          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "16px",
              color: "rgba(255,255,255,0.4)",
              maxWidth: "560px",
              lineHeight: 1.7,
              marginBottom: "16px",
            }}
          >
            Active in the Polkadot ecosystem since 2023. Former 1KV Programme
            validator and first-cohort Kusama Decentralized Nodes operator. Zero
            slashing events — ever.
          </p>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              background: "rgba(34,197,94,0.08)",
              border: "1px solid rgba(34,197,94,0.2)",
              borderRadius: "8px",
              padding: "10px 16px",
              marginBottom: "40px",
            }}
          >
            <span style={{ fontSize: "16px" }}>✓</span>
            <span
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "13px",
                color: "#22c55e",
              }}
            >
              0 slashing events across all networks
            </span>
          </div>

          <div
            ref={trackRef}
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: "16px",
            }}
          >
            <TrackCard
              inView={trackInView}
              delay={0}
              network="Polkadot"
              identity="SafeStake/01"
              address="13pBLqciqvBMZpZT2ZkavANK66PxyG1xZ72bBo849c5NDHgh"
              role="1KV Programme Validator"
              period="2023 – Programme End"
              subscanBase="polkadot"
            />
            <TrackCard
              inView={trackInView}
              delay={100}
              network="Kusama"
              identity="SafeStake.IO/01"
              address="HnqvLipy4zndzJcJWAVJDpDWNMR6ygTVDRym5BeapXHLS2K"
              role="1KV Programme · Decentralized Nodes First Cohort"
              period="2023 – Programme End"
              subscanBase="kusama"
            />
            <TrackCard
              inView={trackInView}
              delay={200}
              network="Kusama"
              identity="SafeStake.IO/02"
              address="H1h4VJPKbk6XxhrxB3QGAonL7ZfGQeABucKHYykdZDxQSyw"
              role="1KV Programme Validator"
              period="2023 – Programme End"
              subscanBase="kusama"
            />
          </div>
        </div>
      </Section>

      {/* ── NOMINATE ── */}
      <Section id="nominate">
        <div
          style={{ maxWidth: "1000px", margin: "0 auto", padding: "80px 40px" }}
        >
          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "11px",
              letterSpacing: "3px",
              color: POLKADOT_PINK,
              textTransform: "uppercase",
              marginBottom: "12px",
            }}
          >
            Nominate
          </div>
          <h2
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 700,
              fontSize: "36px",
              letterSpacing: "-1px",
              marginBottom: "12px",
            }}
          >
            Add SafeStake to Your Nominations
          </h2>
          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "16px",
              color: "rgba(255,255,255,0.4)",
              maxWidth: "560px",
              lineHeight: 1.7,
              marginBottom: "40px",
            }}
          >
            Nominating this validator contributes to Polkadot's infrastructure
            diversity while earning staking rewards at a competitive 5%
            commission rate.
          </p>

          <div
            ref={nomRef}
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "20px",
              marginBottom: "48px",
            }}
          >
            {/* Step cards */}
            {[
              { step: "01", title: "Copy the stash address", desc: STASH },
              {
                step: "02",
                title: "Open the Staking Dashboard",
                desc: "Go to staking.polkadot.cloud or Polkadot-JS Apps",
              },
              {
                step: "03",
                title: "Add to your nomination set",
                desc: "Search for SAFESTAKE or paste the stash address",
              },
            ].map((s, i) => (
              <div
                key={s.step}
                style={{
                  background: "rgba(255,255,255,0.025)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  borderRadius: "12px",
                  padding: "28px 24px",
                  opacity: nomInView ? 1 : 0,
                  transform: nomInView ? "translateY(0)" : "translateY(20px)",
                  transition: `all 0.5s cubic-bezier(.4,0,.2,1) ${i * 100}ms`,
                }}
              >
                <div
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: "32px",
                    fontWeight: 700,
                    color: `${POLKADOT_PINK}88`,
                    marginBottom: "12px",
                  }}
                >
                  {s.step}
                </div>
                <div
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 600,
                    fontSize: "16px",
                    color: "#fff",
                    marginBottom: "8px",
                  }}
                >
                  {s.title}
                </div>
                <div
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: "12px",
                    color: "rgba(255,255,255,0.35)",
                    wordBreak: "break-all",
                    lineHeight: 1.6,
                  }}
                >
                  {s.desc}
                </div>
              </div>
            ))}
          </div>

          {/* CTA buttons */}
          <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
            <a
              href={`https://staking.polkadot.cloud/#/nominate?validator=${STASH}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                background: POLKADOT_PINK,
                color: "#fff",
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 600,
                fontSize: "14px",
                padding: "14px 28px",
                borderRadius: "10px",
                textDecoration: "none",
                transition: "transform 0.2s, box-shadow 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = `0 8px 24px ${POLKADOT_PINK}33`;
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "none";
              }}
            >
              Open Staking Dashboard →
            </a>
            <a
              href={`https://polkadot.subscan.io/account/${STASH}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                background: "transparent",
                color: "rgba(255,255,255,0.6)",
                border: "1px solid rgba(255,255,255,0.12)",
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 500,
                fontSize: "14px",
                padding: "14px 28px",
                borderRadius: "10px",
                textDecoration: "none",
                transition: "border-color 0.2s, color 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = "rgba(255,255,255,0.3)";
                e.target.style.color = "#fff";
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = "rgba(255,255,255,0.12)";
                e.target.style.color = "rgba(255,255,255,0.6)";
              }}
            >
              View on Subscan
            </a>
          </div>
        </div>
      </Section>

      {/* ── FOOTER ── */}
      <footer
        style={{
          borderTop: "1px solid rgba(255,255,255,0.05)",
          padding: "40px",
          maxWidth: "1000px",
          margin: "0 auto",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: "24px",
        }}
      >
        <div>
          <div
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 600,
              fontSize: "16px",
              marginBottom: "8px",
            }}
          >
            SafeStake
          </div>
          <div
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "13px",
              color: "rgba(255,255,255,0.3)",
              lineHeight: 1.7,
            }}
          >
            Owner-operated Polkadot validator
            <br />
            Houston, Texas · Private infrastructure
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "11px",
              color: "rgba(255,255,255,0.2)",
              marginBottom: "8px",
            }}
          >
            <a
              href="https://matrix.to/#/!kciNcuyJkjFWRgZoxm:matrix.org?via=matrix.org"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "rgba(255,255,255,0.4)", textDecoration: "none" }}
            >
              Element
            </a>
            {" · "}
            <a
              href="https://x.com/SafeStakeIO"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "rgba(255,255,255,0.4)", textDecoration: "none" }}
            >
              X / Twitter
            </a>
          </div>
          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "11px",
              color: "rgba(255,255,255,0.15)",
            }}
          >
            © {new Date().getFullYear()} SafeStake.io
          </div>
        </div>
      </footer>
    </div>
  );
}
